// Turning a Firebase write failure into something the person who hit it can
// act on.
//
// WHY THIS EXISTS. Every screen used to report a failed write as "Something
// went wrong. Please try again." — which is wrong twice over for the most
// common failure by far: trying again never helps, and the actual cause
// (database rules that have not been deployed) is a two-minute fix that
// nobody can make if the app will not name it.
//
// PERMISSION_DENIED IS AMBIGUOUS, and that is worth knowing. Firebase returns
// it for three different situations:
//
//   1. no rule grants the write      -> the rules were never deployed
//   2. a rule denies it              -> this account is not allowed here
//   3. a .validate rule rejects it   -> the VALUE is wrong, not the access
//
// Nothing in the error distinguishes them, so the message below names all
// three rather than guessing, in the order they actually happen.

/**
 * Whether a Firebase error is the database refusing a read or write.
 *
 * Checked on both the code and the message: the modular SDK sets `code` on
 * some rejections and only puts PERMISSION_DENIED in the message on others.
 *
 * @param {*} error
 * @returns {boolean}
 */
export const isPermissionDenied = (error) => {
  const code = String(error?.code || '').toUpperCase();
  const message = String(error?.message || '').toUpperCase();
  return code.includes('PERMISSION_DENIED')
    || code === 'PERMISSION-DENIED'
    || message.includes('PERMISSION_DENIED');
};

/**
 * A message for a failed write, naming what to do about it.
 *
 * @param {*} error - whatever the Firebase call rejected with.
 * @param {object} [options]
 * @param {string} [options.what] - what was being saved, e.g. 'the theme'.
 * @param {string} [options.path] - the database node, e.g. 'posTenants/<id>/config'.
 * @returns {string} a message safe to show as-is.
 */
export const describeWriteError = (error, { what = 'this change', path = '' } = {}) => {
  if (isPermissionDenied(error)) {
    const at = path ? ` at ${path}` : '';
    return [
      `The database refused to save ${what}${at}.`,
      '',
      'Almost always this means database.rules.json has not been deployed to this Firebase project yet — run "firebase deploy --only database" (after "firebase use --add" if no project is selected).',
      '',
      'If the rules ARE deployed, then either this account is not one they allow here, or the value itself failed a validation rule.',
    ].join('\n');
  }

  // A user-facing error was raised deliberately by our own code and already
  // says something useful; anything else is genuinely unexpected.
  if (error?.isUserFacing) return error.message;

  const detail = String(error?.message || '').trim();
  return detail
    ? `Could not save ${what}: ${detail}`
    : `Something went wrong saving ${what}. Please try again.`;
};
